import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  DocumentPlusIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  SparklesIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import type { Application, Resume, ResumeFont, ResumeSectionKey, ResumeTemplate, SubscriptionPlan } from '../../types'
import { canUseFeature } from '../../utils/subscription'
import { UpgradePrompt } from '../UpgradePrompt'
import { PageLayout } from '../PageLayout'
import { Skeleton } from '../Skeleton'
import {
  getResume,
  updateResume,
  tailorResumeForApplication,
} from '../../services/resumeBuilderRepo'
import { compareResumeToApplication } from '../../services/resumeService'
import { downloadResumePdf } from '../../utils/resumePdf'
import { trackGAResumeDownloaded, trackGAResumeTailored, trackGAResumeTemplateChanged, trackGAResumeCreated } from '../../utils/googleAnalytics'
import { trackResumeDownloaded, trackResumeTailored, trackResumeCreated } from '../../utils/facebookPixel'
import { ResumePreview, TEMPLATE_OPTIONS } from './templates'
import { DEFAULT_ACCENT, getSectionOrder, labelFor } from './templates/shared'
import {
  ContactSection,
  SummarySection,
  ExperienceSection,
  EducationSection,
  SkillsSection,
  ProjectsSection,
  CertificationsSection,
  LanguagesSection,
  CustomSectionsSection,
} from './ResumeSections'

interface Props {
  userId: string
  plan: SubscriptionPlan
  applications: Application[]
}

const AUTOSAVE_DELAY_MS = 800

export function ResumeBuilderEditorPage({ userId, plan, applications }: Props) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [resume, setResume] = useState<Resume | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [isTailoring, setIsTailoring] = useState(false)
  const [isTailorModalOpen, setIsTailorModalOpen] = useState(false)
  const [selectedTailorAppId, setSelectedTailorAppId] = useState<string>('')
  const [isComparing, setIsComparing] = useState(false)
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false)
  const [selectedCompareAppId, setSelectedCompareAppId] = useState<string>('')
  const [isDownloading, setIsDownloading] = useState(false)

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPro = canUseFeature(plan, 'resume_builder')

  // Load resume
  useEffect(() => {
    if (!id || !userId || !isPro) {
      setLoading(false)
      return
    }
    let cancelled = false
    getResume(id, userId)
      .then((r) => {
        if (cancelled) return
        if (!r) {
          setError('Resume not found')
        } else {
          setResume(r)
        }
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load resume'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [id, userId, isPro])

  // Debounced autosave on resume changes
  const scheduleSave = useCallback((next: Resume) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveState('saving')
    saveTimerRef.current = setTimeout(() => {
      updateResume(next.id, userId, {
        title: next.title,
        template: next.template,
        contact: next.contact,
        summary: next.summary,
        experience: next.experience,
        education: next.education,
        skills: next.skills,
        projects: next.projects,
        certifications: next.certifications,
        languages: next.languages,
        customSections: next.customSections,
      })
        .then(() => setSaveState('saved'))
        .catch((e) => {
          console.error('Autosave failed', e)
          setSaveState('idle')
          setError(e instanceof Error ? e.message : 'Failed to save')
        })
    }, AUTOSAVE_DELAY_MS)
  }, [userId])

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }, [])

  const update = useCallback((patch: Partial<Resume>) => {
    setResume((prev) => {
      if (!prev) return prev
      const next: Resume = { ...prev, ...patch }
      scheduleSave(next)
      return next
    })
  }, [scheduleSave])

  const handleDownload = async () => {
    if (!resume) return
    setIsDownloading(true)
    try {
      await downloadResumePdf(resume)
      trackGAResumeDownloaded(resume.template, resume.contact.font)
      trackResumeDownloaded(resume.template, resume.contact.font)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF generation failed')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleTailor = async () => {
    if (!resume || !selectedTailorAppId) return
    setIsTailoring(true)
    setError(null)
    try {
      const { resumeId } = await tailorResumeForApplication(resume.id, selectedTailorAppId)
      trackGAResumeTailored(selectedTailorAppId)
      trackResumeTailored(selectedTailorAppId)
      trackGAResumeCreated('tailor')
      trackResumeCreated('tailor')
      setIsTailorModalOpen(false)
      navigate(`/resume-builder/${resumeId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Tailoring failed')
    } finally {
      setIsTailoring(false)
    }
  }

  const handleCompare = async () => {
    if (!resume || !selectedCompareAppId) return
    setIsComparing(true)
    setError(null)
    try {
      const result = await compareResumeToApplication(selectedCompareAppId, resume.id)
      setIsCompareModalOpen(false)
      navigate(`/resume/comparison/${result.id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Comparison failed')
    } finally {
      setIsComparing(false)
    }
  }

  const sortedApps = useMemo(
    () => [...applications].filter(a => !a.archived).sort((a, b) => a.company.localeCompare(b.company)),
    [applications]
  )

  const moveSection = useCallback((key: ResumeSectionKey, dir: -1 | 1) => {
    setResume((prev) => {
      if (!prev) return prev
      const current = getSectionOrder(prev)
      const idx = current.indexOf(key)
      const target = idx + dir
      if (idx < 0 || target < 0 || target >= current.length) return prev
      const next = [...current]
      next[idx] = current[target]
      next[target] = key
      const updated: Resume = { ...prev, contact: { ...prev.contact, sectionOrder: next } }
      scheduleSave(updated)
      return updated
    })
  }, [scheduleSave])

  if (!isPro) {
    return (
      <PageLayout icon={DocumentPlusIcon} title="Resume Builder">
        <UpgradePrompt type="feature" featureName="Resume Builder" />
      </PageLayout>
    )
  }

  if (loading) {
    return (
      <PageLayout icon={DocumentPlusIcon} title="Resume Builder">
        <Skeleton className="h-96" />
      </PageLayout>
    )
  }

  if (!resume) {
    return (
      <PageLayout icon={DocumentPlusIcon} title="Resume Builder">
        <div className="rounded-lg border border-rose-300 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-100">
          {error ?? 'Resume not found.'}
        </div>
        <button onClick={() => navigate('/resume-builder')} className="mt-3 text-sm text-app-accent hover:underline">
          Back to resumes
        </button>
      </PageLayout>
    )
  }

  return (
    <PageLayout icon={DocumentPlusIcon} title={resume.title || 'Untitled resume'} maxWidth="max-w-[1400px]">
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/resume-builder')}
            className="inline-flex items-center gap-1 text-sm text-app-muted hover:text-app-ink"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back
          </button>
          <span className="text-xs text-app-muted">
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {sortedApps.length > 0 && (
            <button
              onClick={() => setIsTailorModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle transition-colors"
            >
              <SparklesIcon className="h-4 w-4" /> Tailor for application
            </button>
          )}
          {sortedApps.length > 0 && (
            <button
              onClick={() => setIsCompareModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-app-line bg-app-highlight px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight transition-colors"
            >
              <DocumentMagnifyingGlassIcon className="h-4 w-4" /> Analyze against a job
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-app-primary px-3 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 transition-colors"
          >
            {isDownloading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            {isDownloading ? 'Building…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-300 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-100">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor pane */}
        <div className="space-y-3">
          <div className="border border-app-line bg-app-surface p-4 space-y-3 rounded-2xl shadow-app-card">
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1">Resume title</label>
              <input
                value={resume.title}
                onChange={(e) => update({ title: e.target.value })}
                className="w-full border border-app-line bg-app-surface px-2.5 py-1.5 text-sm rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1">Template</label>
              <div className="flex flex-wrap gap-2">
                {TEMPLATE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      const t = opt.value as ResumeTemplate
                      if (resume.template !== t) trackGAResumeTemplateChanged(t)
                      update({ template: t })
                    }}
                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                      resume.template === opt.value
                        ? 'border-app-accent bg-app-highlight text-app-accent'
                        : 'border-app-line text-app-muted hover:bg-app-subtle'
                    }`}
                  >
                    <div className="font-semibold">{opt.label}</div>
                    <div className="text-[10px] text-app-muted">{opt.description}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-app-muted mb-1">Accent color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={resume.contact.accentColor || DEFAULT_ACCENT}
                    onChange={(e) => update({ contact: { ...resume.contact, accentColor: e.target.value } })}
                    className="h-8 w-12 border border-app-line bg-app-surface cursor-pointer rounded-lg"
                    aria-label="Accent color"
                  />
                  <button
                    type="button"
                    onClick={() => update({ contact: { ...resume.contact, accentColor: DEFAULT_ACCENT } })}
                    className="text-xs text-app-muted hover:text-app-ink"
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-app-muted mb-1">Font</label>
                <select
                  value={resume.contact.font || 'sans'}
                  onChange={(e) => update({ contact: { ...resume.contact, font: e.target.value as ResumeFont } })}
                  className="w-full border border-app-line bg-app-surface px-2.5 py-1.5 text-sm rounded-lg"
                >
                  <option value="sans">Sans-serif (Helvetica)</option>
                  <option value="serif">Serif (Times)</option>
                  <option value="mono">Monospace (Courier)</option>
                  <option value="lato">Lato (modern sans)</option>
                  <option value="ptserif">PT Serif (refined serif)</option>
                </select>
              </div>
            </div>
          </div>

          <ContactSection value={resume.contact} onChange={(contact) => update({ contact })} />
          {(() => {
            const order = getSectionOrder(resume)
            return order.map((key, idx) => {
              const canUp = idx > 0
              const canDown = idx < order.length - 1
              const controls = (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveSection(key, -1)}
                    disabled={!canUp}
                    aria-label={`Move ${key} up`}
                    title="Move up"
                    className="p-1 text-app-muted hover:bg-app-subtle disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSection(key, 1)}
                    disabled={!canDown}
                    aria-label={`Move ${key} down`}
                    title="Move down"
                    className="p-1 text-app-muted hover:bg-app-subtle disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
                  >
                    ↓
                  </button>
                </div>
              )
              const body = (() => {
                switch (key) {
                  case 'summary': return <SummarySection value={resume.summary} onChange={(summary) => update({ summary })} />
                  case 'experience': return <ExperienceSection value={resume.experience} onChange={(experience) => update({ experience })} />
                  case 'education': return <EducationSection value={resume.education} onChange={(education) => update({ education })} />
                  case 'skills': return <SkillsSection value={resume.skills} onChange={(skills) => update({ skills })} />
                  case 'projects': return <ProjectsSection value={resume.projects} onChange={(projects) => update({ projects })} />
                  case 'certifications': return <CertificationsSection value={resume.certifications} onChange={(certifications) => update({ certifications })} />
                  case 'languages': return <LanguagesSection value={resume.languages} onChange={(languages) => update({ languages })} />
                  case 'customSections': return <CustomSectionsSection value={resume.customSections} onChange={(customSections) => update({ customSections })} />
                }
              })()
              return (
                <div key={key}>
                  <div className="flex items-center justify-between px-1 mb-1">
                    <span className="text-[10px] uppercase tracking-wider text-app-muted">{labelFor(resume, key)}</span>
                    {controls}
                  </div>
                  {body}
                </div>
              )
            })
          })()}        </div>

        {/* Preview pane — sticks to the top of the viewport while editing */}
        <div className="sticky top-4 self-start">
          <div data-resume-document className="max-h-[calc(100vh-2rem)] overflow-y-auto">
            <ResumePreview resume={resume} />
          </div>
        </div>
      </div>

      {/* Tailor modal */}
      {isTailorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isTailoring && setIsTailorModalOpen(false)}>
          <div className="bg-app-surface max-w-lg w-full p-5 rounded-2xl shadow-app-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-app-ink font-display">Tailor for application</h3>
            <p className="text-sm text-app-muted mt-1">
              We'll create a copy of this resume and use AI to rewrite the summary and bullets to emphasize relevance to the chosen job.
              No facts, employers, or dates will be invented.
            </p>
            <label className="block text-xs font-medium text-app-muted mt-3 mb-1">Application</label>
            <select
              value={selectedTailorAppId}
              onChange={(e) => setSelectedTailorAppId(e.target.value)}
              className="w-full border border-app-line bg-app-surface px-2.5 py-1.5 text-sm rounded-lg"
            >
              <option value="">Select an application…</option>
              {sortedApps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.company}{a.positionTitle ? ` — ${a.positionTitle}` : ''}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setIsTailorModalOpen(false)} disabled={isTailoring} className="px-3 py-1.5 text-sm font-medium text-app-muted hover:bg-app-subtle disabled:opacity-50 rounded-lg">Cancel</button>
              <button onClick={handleTailor} disabled={!selectedTailorAppId || isTailoring} className="inline-flex items-center gap-1.5 bg-app-primary px-3 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 transition-colors rounded-lg">
                {isTailoring ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <SparklesIcon className="h-4 w-4" />
                )}
                {isTailoring ? 'Tailoring…' : 'Tailor with AI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare modal */}
      {isCompareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isComparing && setIsCompareModalOpen(false)}>
          <div className="bg-app-surface max-w-lg w-full p-5 rounded-2xl shadow-app-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-app-ink font-display">Analyze against a job</h3>
            <p className="text-sm text-app-muted mt-1">
              We'll match this resume against the selected application's job description and produce a match score with strengths, gaps, and suggestions.
            </p>
            <label className="block text-xs font-medium text-app-muted mt-3 mb-1">Application</label>
            <select
              value={selectedCompareAppId}
              onChange={(e) => setSelectedCompareAppId(e.target.value)}
              className="w-full border border-app-line bg-app-surface px-2.5 py-1.5 text-sm rounded-lg"
            >
              <option value="">Select an application…</option>
              {sortedApps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.company}{a.positionTitle ? ` — ${a.positionTitle}` : ''}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setIsCompareModalOpen(false)} disabled={isComparing} className="px-3 py-1.5 text-sm font-medium text-app-muted hover:bg-app-subtle disabled:opacity-50 rounded-lg">Cancel</button>
              <button onClick={handleCompare} disabled={!selectedCompareAppId || isComparing} className="inline-flex items-center gap-1.5 bg-app-primary px-3 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 transition-colors rounded-lg">
                {isComparing ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                )}
                {isComparing ? 'Analyzing…' : 'Run analysis'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
